/**
 * WhitelistDialog — dauerhafte Whitelist-Markierung mit Pflicht-Kommentar (Plan 02-05
 * Task 2, PROT-02). Gleiches Muster wie SnoozeDialog, aber bewusst KEIN Datumsfeld —
 * die Markierung ist dauerhaft ("nie wieder").
 *
 * `anzahl` (Task 3, Integrations-Ergaenzung): der Backend-Endpunkt `POST /whitelist`
 * nimmt nur EIN `itemId` entgegen (kein Bulk-Array wie bei `/snooze`) — bei
 * Mehrfachauswahl ruft `WarnlistePage` `onConfirm` daher sequenziell je Artikel mit
 * demselben Kommentar auf; der Dialog zeigt die Anzahl nur fuer die Nutzertext-Klarheit.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@libero/core/components/ui/dialog';
import { Button } from '@libero/core/components/ui/button';

interface WhitelistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anzahl: number;
  onConfirm: (kommentar: string) => Promise<void>;
}

export default function WhitelistDialog({ open, onOpenChange, anzahl, onConfirm }: WhitelistDialogProps) {
  const [kommentar, setKommentar] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKommentar('');
      setError(null);
    }
  }, [open]);

  async function handleSave() {
    const trimmed = kommentar.trim();
    if (!trimmed) {
      setError('Kommentar ist Pflicht.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onConfirm(trimmed);
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Whitelist-Markierung konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-[#1A1A1A]">
            {anzahl === 1 ? 'Artikel whitelisten' : `${anzahl} Artikel whitelisten`}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-600">
          {anzahl === 1
            ? 'Dieser Artikel erscheint dauerhaft nie wieder in der Liste, bis die Markierung manuell aufgehoben wird.'
            : `Alle ${anzahl} ausgewaehlten Artikel erscheinen dauerhaft nie wieder in der Liste, bis die Markierung manuell aufgehoben wird.`}
        </p>

        <div className="space-y-1">
          <label htmlFor="whitelist-kommentar" className="text-xs font-bold text-gray-600 uppercase tracking-wide">
            Kommentar (Pflicht)
          </label>
          <textarea
            id="whitelist-kommentar"
            value={kommentar}
            onChange={(e) => setKommentar(e.target.value)}
            rows={3}
            className="w-full rounded border border-[#DAD9D9] px-2 py-1.5 text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#E9FF57]"
            placeholder="Warum wird dieser Artikel dauerhaft behalten?"
            autoFocus
          />
        </div>

        {error && <p className="text-sm text-red-700">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || !kommentar.trim()}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            {saving ? 'Speichert…' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
